import { Button } from "@hoylu/client-common";
import { clsx } from "clsx";
import React, { useState } from "react";
import { useSelector } from "react-redux";
import { selectUserToken } from "../../../state/user/user.selector";
import { useI18n } from "../../../utils/hooks/use.i18n";
import { isOrganizationAdmin } from "../../../utils/sharing.helpers";
import { PasswordInput } from "./PasswordInput";
import Styles from "./WorkspacePassword.module.css";

export const MODE = {
  DEFAULT: "DEFAULT",
  ADD: "ADD",
  REMOVE: "REMOVE",
  CHANGE: "CHANGE",
};

type WorkspacePasswordProps = {
  confirmPasswordAction: (
    password: string,
    currentPassword: string,
    mode: string,
  ) => void;
  hasPassword: boolean;
  requiresPassword: boolean;
};

export const WorkspacePassword: React.FC<WorkspacePasswordProps> = ({
  confirmPasswordAction,
  hasPassword,
  requiresPassword,
}) => {
  const t = useI18n("WORKSPACE_PASSWORD.");

  const userToken = useSelector(selectUserToken);
  const isOrgAdmin = isOrganizationAdmin(userToken);

  const [mode, setMode] = useState(MODE.DEFAULT);
  const [isAddingPassword, toggleAddPassword] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [currentPassword, setCurrentPassword] = useState("");
  const [confirmPassword, updateConfirmPassword] = useState("");

  function handleChooseMode(newMode: string) {
    switch (newMode) {
      case MODE.ADD:
        toggleAddPassword(true);
        setMode(MODE.ADD);
        break;
      case MODE.REMOVE:
        toggleAddPassword(true);
        setMode(MODE.REMOVE);
        break;
      case MODE.CHANGE:
        toggleAddPassword(true);
        setMode(MODE.CHANGE);
        break;
      default:
        setMode(MODE.DEFAULT);
    }
  }

  const passwordValidation = /^([a-zA-Za-zA-Z@#$%^&-+=()\d]).{7,32}/;

  const allowAddPassword: boolean = hasPassword
    ? !requiresPassword || passwordValidation.test(newPassword)
    : passwordValidation.test(newPassword) && newPassword === confirmPassword;

  const allowChangePassword: boolean =
    allowAddPassword &&
    newPassword.length !== 0 &&
    confirmPassword.length !== 0 &&
    newPassword === confirmPassword;

  const triggerPasswordAction = (mode: string): void => {
    confirmPasswordAction(newPassword, currentPassword, mode);
    wipeOutData();
  };

  const wipeOutData = (): void => {
    toggleAddPassword(false);
    setNewPassword("");
    setCurrentPassword("");
    updateConfirmPassword("");
  };

  const setHeaderText = (mode: string): string => {
    switch (mode) {
      case MODE.ADD:
        return t("ADD_WORKSPACE_PASSWORD");
      case MODE.REMOVE:
        return t("REMOVE_WORKSPACE_PASSWORD");
      case MODE.CHANGE:
        return t("CHANGE_WORKSPACE_PASSWORD");
      default:
        return "";
    }
  };

  return (
    <>
      <div className={clsx(!isAddingPassword && Styles.hidden)}>
        <div className={Styles.addPassword}>{setHeaderText(mode)}</div>
        {mode !== MODE.REMOVE && (
          <div className={Styles.minCharacters}>{t("CHARACTERS")}</div>
        )}
        {mode === MODE.REMOVE && !isOrgAdmin && (
          <PasswordInput
            password={newPassword}
            passwordOnChange={setNewPassword}
            placeholder={t("ENTER_CURRENT_PASSWORD")}
          />
        )}
        {mode === MODE.ADD && (
          <React.Fragment>
            <PasswordInput
              password={newPassword}
              passwordOnChange={setNewPassword}
              placeholder={t("SET_NEW_PASSWORD")}
            />
            <PasswordInput
              password={confirmPassword}
              passwordOnChange={updateConfirmPassword}
              placeholder={t("CONFIRM_NEW_PASSWORD")}
            />
          </React.Fragment>
        )}
        {mode === MODE.CHANGE && (
          <React.Fragment>
            <PasswordInput
              password={currentPassword}
              passwordOnChange={setCurrentPassword}
              placeholder={t("ENTER_CURRENT_PASSWORD")}
            />
            <PasswordInput
              password={newPassword}
              passwordOnChange={setNewPassword}
              placeholder={t("SET_NEW_PASSWORD")}
            />
            <PasswordInput
              password={confirmPassword}
              passwordOnChange={updateConfirmPassword}
              placeholder={t("CONFIRM_NEW_PASSWORD")}
            />
          </React.Fragment>
        )}
        <div className={Styles.setPasswordFooter}>
          <Button
            onClick={() => wipeOutData()}
            toolTip={t("CANCEL")}
            type="negative"
          >
            {t("CANCEL")}
          </Button>
          {(mode === MODE.ADD || mode === MODE.REMOVE) && (
            <Button
              disabled={!allowAddPassword}
              onClick={() => triggerPasswordAction(mode)}
              toolTip={hasPassword ? t("REMOVE_PASSWORD") : t("ADD_PASSWORD")}
              type="primary"
            >
              {hasPassword ? t("REMOVE_PASSWORD") : t("ADD_PASSWORD")}
            </Button>
          )}
          {mode === MODE.CHANGE && (
            <Button
              disabled={!allowChangePassword}
              onClick={() => triggerPasswordAction(mode)}
              toolTip={t("CHANGE_PASSWORD")}
              type="primary"
            >
              {t("CHANGE_PASSWORD")}
            </Button>
          )}
        </div>
      </div>
      <div
        className={clsx(
          Styles.changeSetPassword,
          isAddingPassword && Styles.hidden,
        )}
      >
        <span className={Styles.passwordInformation}>
          {hasPassword
            ? t("WORKSPACE_PASSWORD_SET")
            : t("WORKSPACE_NO_PASSWORD")}
        </span>
        {hasPassword && (
          <Button
            onClick={() => handleChooseMode(MODE.CHANGE)}
            toolTip={t("CHANGE")}
            type="primary"
          >
            {t("CHANGE")}
          </Button>
        )}

        <Button
          disabled={hasPassword && requiresPassword}
          onClick={() =>
            hasPassword
              ? handleChooseMode(MODE.REMOVE)
              : handleChooseMode(MODE.ADD)
          }
          toolTip={hasPassword ? t("REMOVE_PASSWORD") : t("ADD_PASSWORD")}
          type="primary"
        >
          {hasPassword ? t("REMOVE") : t("ADD_PASSWORD")}
        </Button>
      </div>
    </>
  );
};
